/**
 * Inbound email / confirmations API — mirrors iOS UserAPI + InboundEmailService
 * (vertical slice: address, list, detail, mark complete).
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';
import {
  isValidEmailAddress,
  normalizeEmailAddress,
  normalizedUniqueEmails,
} from '../utils/emailAddress';

export type InboundConfirmationStatus = 'received' | 'completed' | 'imported';

export interface InboundEmailInfo {
  inboundCode: string;
  inboundEmailAddress: string;
}

export interface InboundAlternateSender {
  /** Server id when present; otherwise use email as list key. */
  serverId: string | null;
  email: string;
  verified?: boolean | null;
}

export interface InboundEmailSendersList {
  accountEmail: string | null;
  alternates: InboundAlternateSender[];
}

export interface InboundConfirmationItem {
  id: string;
  fileName?: string | null;
  mimeType?: string | null;
  status?: string | null;
}

export interface InboundConfirmation {
  id: string;
  fromAddress: string;
  subject?: string | null;
  status: string;
  receivedAt: string;
  inboundCode: string;
  accountEmail: string;
  bodyPreview?: string | null;
  bodyHtml?: string | null;
  attachments?: InboundConfirmationItem[] | null;
  attachmentCount?: number | null;
  primarySuggestedTripId?: string | null;
  importedTripId?: string | null;
  type?: string | null;
}

export class InboundEmailNotProvisionedError extends Error {
  constructor(message = 'Inbound email not provisioned') {
    super(message);
    this.name = 'InboundEmailNotProvisionedError';
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function strOrNull(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value);
  return s.length > 0 ? s : null;
}

export function parseInboundConfirmation(raw: unknown): InboundConfirmation {
  const o = asRecord(raw);
  const attachmentsRaw = Array.isArray(o.attachments) ? o.attachments : null;
  return {
    id: str(o.id),
    fromAddress: str(o.fromAddress ?? o.from),
    subject: strOrNull(o.subject),
    status: str(o.status, 'received'),
    receivedAt: str(o.receivedAt ?? o.received_at),
    inboundCode: str(o.inboundCode ?? o.inbound_code),
    accountEmail: str(o.accountEmail ?? o.account_email),
    bodyPreview: strOrNull(o.bodyPreview ?? o.body_preview),
    bodyHtml: strOrNull(o.bodyHtml ?? o.body_html),
    attachments: attachmentsRaw
      ? attachmentsRaw.map((item) => {
          const a = asRecord(item);
          return {
            id: str(a.id),
            fileName: strOrNull(a.fileName ?? a.filename),
            mimeType: strOrNull(a.mimeType ?? a.contentType),
            status: strOrNull(a.status),
          };
        })
      : null,
    attachmentCount:
      typeof o.attachmentCount === 'number'
        ? o.attachmentCount
        : typeof o.attachment_count === 'number'
          ? o.attachment_count
          : attachmentsRaw
            ? attachmentsRaw.length
            : null,
    primarySuggestedTripId: strOrNull(o.primarySuggestedTripId),
    importedTripId: strOrNull(o.importedTripId),
    type: strOrNull(o.type),
  };
}

export function confirmationStatus(c: InboundConfirmation): InboundConfirmationStatus {
  const s = c.status.toLowerCase();
  if (s === 'completed' || s === 'imported') return s;
  return 'received';
}

export function pendingConfirmationsCount(items: InboundConfirmation[]): number {
  return items.filter((c) => confirmationStatus(c) === 'received').length;
}

export async function getInboundEmail(): Promise<InboundEmailInfo | null> {
  const headers = await buildAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/users/me/inbound-email`, {
    method: 'GET',
    headers,
  });
  if (response.status === 404) {
    throw new InboundEmailNotProvisionedError();
  }
  if (!response.ok) {
    throw new Error(`getInboundEmail failed: ${response.status}`);
  }
  const json = asRecord(await response.json());
  const address = str(json.inboundEmailAddress ?? json.inbound_email_address);
  const code = str(json.inboundCode ?? json.inbound_code);
  if (!address) return null;
  return { inboundCode: code, inboundEmailAddress: address };
}

export async function listInboundConfirmations(options: {
  cursor?: string;
  limit?: number;
  status?: string;
} = {}): Promise<{ items: InboundConfirmation[]; nextCursor: string | null }> {
  const headers = await buildAuthHeaders();
  const params = new URLSearchParams();
  const limit = Math.min(Math.max(options.limit ?? 20, 1), 1000);
  params.set('limit', String(limit));
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.status) params.set('status', options.status);

  const response = await fetch(
    `${API_BASE_URL}/users/me/inbound-confirmations?${params.toString()}`,
    { method: 'GET', headers }
  );
  if (response.status === 404) {
    throw new InboundEmailNotProvisionedError();
  }
  if (!response.ok) {
    throw new Error(`listInboundConfirmations failed: ${response.status}`);
  }

  const json = await response.json();
  const list = Array.isArray(json)
    ? json
    : Array.isArray(asRecord(json).items)
      ? (asRecord(json).items as unknown[])
      : Array.isArray(asRecord(json).confirmations)
        ? (asRecord(json).confirmations as unknown[])
        : [];
  const nextCursor =
    response.headers.get('x-next-cursor') ||
    strOrNull(asRecord(json).nextCursor) ||
    strOrNull(asRecord(json).next_cursor) ||
    null;

  return {
    items: list.map(parseInboundConfirmation),
    nextCursor,
  };
}

export async function getInboundConfirmation(id: string): Promise<InboundConfirmation> {
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/users/me/inbound-confirmations/${encodeURIComponent(id)}`,
    { method: 'GET', headers }
  );
  if (response.status === 404) {
    throw new InboundEmailNotProvisionedError();
  }
  if (!response.ok) {
    throw new Error(`getInboundConfirmation failed: ${response.status}`);
  }
  return parseInboundConfirmation(await response.json());
}

export async function patchInboundConfirmation(
  id: string,
  status: InboundConfirmationStatus,
  importedTripId?: string
): Promise<InboundConfirmation> {
  const headers = await buildAuthHeaders(true);
  const body: Record<string, unknown> = { status };
  if (importedTripId) body.importedTripId = importedTripId;

  const response = await fetch(
    `${API_BASE_URL}/users/me/inbound-confirmations/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    throw new Error(`patchInboundConfirmation failed: ${response.status}`);
  }
  // Some APIs return empty body on PATCH — re-fetch when needed.
  const text = await response.text();
  if (!text.trim()) {
    return getInboundConfirmation(id);
  }
  try {
    return parseInboundConfirmation(JSON.parse(text));
  } catch {
    return getInboundConfirmation(id);
  }
}

/** Lightweight pending count for the envelope badge. */
export async function fetchInboundPendingCount(): Promise<number> {
  try {
    const { items } = await listInboundConfirmations({
      limit: 50,
      status: 'received',
    });
    return pendingConfirmationsCount(items);
  } catch (err) {
    if (err instanceof InboundEmailNotProvisionedError) return 0;
    console.warn('inboundEmailService: pending count failed', err);
    return 0;
  }
}

function parseAlternateSender(raw: unknown): InboundAlternateSender | null {
  if (typeof raw === 'string') {
    const email = normalizeEmailAddress(raw);
    if (!isValidEmailAddress(email)) return null;
    return { serverId: null, email, verified: null };
  }
  const o = asRecord(raw);
  const email = normalizeEmailAddress(str(o.email));
  if (!isValidEmailAddress(email)) return null;
  const id = strOrNull(o.id);
  return {
    serverId: id,
    email,
    verified: typeof o.verified === 'boolean' ? o.verified : null,
  };
}

/** Normalize GET /users/me/inbound-email-senders payloads (wrapper, string[], or object[]). */
export function parseInboundEmailSendersList(raw: unknown): InboundEmailSendersList {
  if (Array.isArray(raw)) {
    const alternates = normalizedUniqueEmails(
      raw
        .map((item) => parseAlternateSender(item)?.email ?? '')
        .filter(Boolean)
    ).map((email) => {
      const fromObj = raw
        .map(parseAlternateSender)
        .find((s) => s?.email === email);
      return fromObj ?? { serverId: null, email, verified: null };
    });
    return { accountEmail: null, alternates };
  }

  const o = asRecord(raw);
  const accountEmailRaw = strOrNull(o.accountEmail ?? o.account_email);
  const accountEmail = accountEmailRaw
    ? normalizeEmailAddress(accountEmailRaw)
    : null;
  const listRaw = Array.isArray(o.alternates)
    ? o.alternates
    : Array.isArray(o.senders)
      ? o.senders
      : Array.isArray(o.items)
        ? o.items
        : [];
  const parsed = listRaw
    .map(parseAlternateSender)
    .filter((s): s is InboundAlternateSender => s != null);
  const seen = new Set<string>();
  const alternates: InboundAlternateSender[] = [];
  for (const sender of parsed) {
    if (seen.has(sender.email)) continue;
    seen.add(sender.email);
    alternates.push(sender);
  }
  alternates.sort((a, b) => a.email.localeCompare(b.email));
  return { accountEmail, alternates };
}

export async function listInboundEmailSenders(): Promise<InboundEmailSendersList> {
  const headers = await buildAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/users/me/inbound-email-senders`, {
    method: 'GET',
    headers,
  });
  if (response.status === 404) {
    throw new InboundEmailNotProvisionedError();
  }
  if (!response.ok) {
    throw new Error(`listInboundEmailSenders failed: ${response.status}`);
  }
  return parseInboundEmailSendersList(await response.json());
}

export async function addInboundEmailSender(email: string): Promise<void> {
  const normalized = normalizeEmailAddress(email);
  if (!isValidEmailAddress(normalized)) {
    throw new Error('invalid-email');
  }
  const headers = await buildAuthHeaders(true);
  const response = await fetch(`${API_BASE_URL}/users/me/inbound-email-senders`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email: normalized }),
  });
  if (!response.ok) {
    throw new Error(`addInboundEmailSender failed: ${response.status}`);
  }
}

export async function deleteInboundEmailSender(id: string): Promise<void> {
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/users/me/inbound-email-senders/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers }
  );
  if (response.status === 404) {
    throw new InboundEmailNotProvisionedError();
  }
  if (!response.ok) {
    throw new Error(`deleteInboundEmailSender failed: ${response.status}`);
  }
}
